import React, { useState, useEffect, useRef } from 'react';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { Link, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faCoins, faHeart } from '@fortawesome/free-solid-svg-icons';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { useGetMeQuery } from 'store/services/user/meApi';
import { AssetUrls } from 'config/assets';
import Points from 'components/Points';
import { getOrCreateAnonymousId } from 'utils/referralCookies';
import {
  useResolveAffiliateSlugQuery,
  useLogAffiliateClickMutation,
} from 'store/services/affiliates/affiliatesApi';
import { LOGIN_TAB_PARAM, LOGIN_TAB_SIGN_IN } from 'screens/Auth/Login/loginTabParam';
import {
  ED_ELITE_LINK_HREF,
  ED_ELITE_STRIP,
  ED_HERO,
  ED_HOW,
  ED_OFFER,
  ED_PAGE_DESCRIPTION,
  ED_PAGE_KEYWORDS,
  ED_PAGE_TITLE,
  ED_PROOF,
} from './eternalDurdlesCopy';
import 'screens/Public/Elite/elite.css';
import './eternal-durdles.css';

const BNG_47_SCRYFALL_ID = '546dae51-070c-4f3f-b0b4-6b88ff314af1';

const ED_LOGO_URL = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/images/ED25LOGO.png';
const ED_ZAC_URL = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/images/Zac+THUMBS.webp';
const ED_PHIL_URL = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/images/Phil+Thumb.webp';

const SIGN_IN_HREF = `/welcome?${LOGIN_TAB_PARAM}=${LOGIN_TAB_SIGN_IN}`;

// A deliberate trading sign-in drops any stored return address (Asana 1220218671326301, Decision 3).
const clearStoredReturnTo = () => ImportSessionStorage.clearReturnTo();

const STEP_ICONS = [faLayerGroup, faCoins, faHeart];

/**
 * Partner landing for the Eternal Durdles / Reternal Durdles ad reads
 * (Asana 1219910162763584): a free-account pitch with the onboarding credit,
 * the three-step loop, and Elite as a low-emphasis strip below the fold. The
 * URL and the QR Zac already has (/eternaldurdles?ref=<username>) are unchanged;
 * attribution rides the global ?ref= capture, not anything on this page.
 *
 * The 500-point Elite conversion bonus this page used to sell (Stripe checkout
 * with promoSource eternaldurdles) is retired (Eric, 2026-10-28); no checkout
 * starts from here any more.
 */
const EternalDurdlesPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [searchParams] = useSearchParams();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const refParam = searchParams.get('ref');

  const { data: meData } = useGetMeQuery(undefined, {
    skip: !currentUser,
  });

  const isElite = meData?.isElite || false;

  // Click parity with /elite/:slug (ruled): a ?ref= arrival logs an affiliate
  // click so dashboard click counts include second-QR scans. tm_ref itself is
  // already set by the global AnalyticsTracker ?ref= capture; this only adds
  // the click row. Resolve-gated so invalid refs log nothing, and the backend
  // dedupes per anonymous id / 24h.
  const { data: resolvedRef } = useResolveAffiliateSlugQuery(refParam || '', {
    skip: !refParam,
  });
  const [logClick] = useLogAffiliateClickMutation();
  const clickLoggedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!refParam) return;
    if (!resolvedRef?.valid || !resolvedRef.username) return;
    if (clickLoggedRef.current === resolvedRef.username) return;
    clickLoggedRef.current = resolvedRef.username;

    logClick({
      slug: resolvedRef.username,
      landingUrl: window.location.href,
      anonymousId: getOrCreateAnonymousId(),
    });
  }, [refParam, resolvedRef, logClick]);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  const heroBgUrl = AssetUrls.cards.getCardImage(BNG_47_SCRYFALL_ID, 'art_crop');

  const renderHero = () => (
    <section className="eternal-durdles-hero">
      <div className="ed-hero__cobrand">
        <img
          className="ed-cobrand__wordmark"
          src={AssetUrls.logos.arevakhachIcon}
          alt="TradeMagic"
          loading="eager"
        />
        <span className="ed-hero__divider" aria-hidden="true">
          ×
        </span>
        <img
          className="ed-cobrand__logo"
          src={ED_LOGO_URL}
          alt="Eternal Durdles podcast logo"
          loading="eager"
        />
      </div>
      <div className="ed-hero__welcome">
        <h1 className="ed-hero__headline">{ED_HERO.headline}</h1>
        <p className="ed-hero__subhead">{ED_HERO.subhead}</p>
      </div>
    </section>
  );

  const renderSignupCard = () => (
    <div className="ed-cta-wrapper">
      <img
        className="ed-portrait ed-portrait--zac"
        src={ED_ZAC_URL}
        alt="Zac from the Eternal Durdles podcast"
        loading="lazy"
      />

      <div className="ed-signup-card" data-testid="ed-signup-card">
        <p className="ed-signup-card__eyebrow">{ED_OFFER.eyebrow}</p>
        <p className="ed-signup-card__offer">
          {ED_OFFER.lead} <Points type="inline" points={ED_OFFER.points} />
          {ED_OFFER.tail}
        </p>
        <p className="ed-signup-card__sub">{ED_OFFER.sub}</p>
        {currentUser ? (
          <Link
            to="/home"
            className="elite-subscribe-button elite-subscribe-button--shimmer ed-signup-card__cta"
          >
            {ED_OFFER.ctaSignedIn}
          </Link>
        ) : (
          <Link
            to="/welcome"
            className="elite-subscribe-button elite-subscribe-button--shimmer ed-signup-card__cta"
            onClick={clearStoredReturnTo}
          >
            {ED_OFFER.cta}
          </Link>
        )}
        <p className="ed-signup-card__formats">{ED_OFFER.formats}</p>
        {!currentUser && (
          <p className="ed-signup-card__signin">
            {ED_OFFER.haveAccount}
            <Link to={SIGN_IN_HREF} onClick={clearStoredReturnTo}>
              {ED_OFFER.signIn}
            </Link>
          </p>
        )}
      </div>

      <img
        className="ed-portrait ed-portrait--phil"
        src={ED_PHIL_URL}
        alt="Phil from the Eternal Durdles podcast"
        loading="lazy"
      />
    </div>
  );

  const renderHowItWorks = () => (
    <section className="ed-how" aria-labelledby="ed-how-heading">
      <h2 id="ed-how-heading" className="ed-how__heading">
        {ED_HOW.heading}
      </h2>
      <div className="ed-how__steps">
        {ED_HOW.steps.map((step, index) => (
          <div key={step.title} className="ed-step" data-testid="ed-step">
            <span className="ed-step__icon" aria-hidden="true">
              <FontAwesomeIcon icon={STEP_ICONS[index]} />
            </span>
            <h3 className="ed-step__title">{step.title}</h3>
            <p className="ed-step__body">{step.body}</p>
          </div>
        ))}
      </div>
    </section>
  );

  const renderEliteStrip = () => (
    <div className="ed-elite-strip" data-testid="ed-elite-strip">
      <img src={AssetUrls.icons.elite.badge} alt="" className="ed-elite-strip__badge" />
      <p className="ed-elite-strip__text">{ED_ELITE_STRIP.text}</p>
      <Link to={ED_ELITE_LINK_HREF} className="ed-elite-strip__link">
        {ED_ELITE_STRIP.link}
      </Link>
    </div>
  );

  return (
    <>
      <SEOHelmet
        title={ED_PAGE_TITLE}
        description={ED_PAGE_DESCRIPTION}
        canonical="https://trademagic.gg/eternaldurdles"
        keywords={ED_PAGE_KEYWORDS}
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Eternal Durdles', url: 'https://trademagic.gg/eternaldurdles' },
        ]}
      />

      <div className="elite-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="elite-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="elite-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/eternaldurdles', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div
            className="elite-main elite-main--showcase"
            style={{ '--elite-hero-bg': `url(${heroBgUrl})` } as React.CSSProperties}
          >
            <TopNavigationBar
              title="Eternal Durdles"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="elite-container">
              <div className="elite-showcase-section">
                {renderHero()}
                {renderSignupCard()}
                {renderHowItWorks()}
                <p className="ed-proof">{ED_PROOF}</p>
                {!isElite && renderEliteStrip()}
              </div>
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default EternalDurdlesPage;
